/**
 * Password Validation Utility
 * Provides real-time password strength analysis and validation
 */

export const validatePassword = (password) => {
    const checks = {
        length: password.length >= 6,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const passed = Object.values(checks).filter(Boolean).length;

    let strength = 'weak';
    let color = 'red';
    let percentage = 0;

    if (passed >= 5) {
        strength = 'strong';
        color = 'green';
        percentage = 100;
    } else if (passed >= 3) {
        strength = 'medium';
        color = 'yellow';
        percentage = 60;
    } else if (passed >= 1) {
        strength = 'weak';
        color = 'red';
        percentage = 30;
    }

    return {
        checks,
        strength,
        color,
        percentage,
        isValid: checks.length && passed >= 3 // At least 6 chars + 2 other requirements
    };
};

export const PasswordStrengthIndicator = ({ password, showRequirements = true }) => {
    const validation = validatePassword(password);

    if (!password) return null;

    const colorClasses = {
        red: 'bg-red-500',
        yellow: 'bg-yellow-500',
        green: 'bg-green-500'
    };

    const textColorClasses = {
        red: 'text-red-600',
        yellow: 'text-yellow-600',
        green: 'text-green-600'
    };

    return (
        <div className="mt-3 space-y-3">
            {/* Strength Bar */}
            <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider">Password Strength</span>
                    <span className={`text-[9px] font-black uppercase tracking-wider ${textColorClasses[validation.color]}`}>
                        {validation.strength}
                    </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full ${colorClasses[validation.color]} transition-all duration-300 rounded-full`}
                        style={{ width: `${validation.percentage}%` }}
                    ></div>
                </div>
            </div>

            {/* Requirements Checklist */}
            {showRequirements && (
                <div className="bg-gray-50/50 rounded-xl p-3 border border-gray-100">
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-wider mb-2">Requirements</p>
                    <div className="grid grid-cols-1 gap-1.5">
                        <RequirementItem met={validation.checks.length} text="At least 6 characters" />
                        <RequirementItem met={validation.checks.uppercase} text="One uppercase letter" />
                        <RequirementItem met={validation.checks.lowercase} text="One lowercase letter" />
                        <RequirementItem met={validation.checks.number} text="One number" />
                        <RequirementItem met={validation.checks.special} text="One special character" />
                    </div>
                </div>
            )}
        </div>
    );
};

const RequirementItem = ({ met, text }) => (
    <div className="flex items-center gap-2">
        <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] transition-all ${met ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-300'
            }`}>
            {met ? '✓' : '○'}
        </div>
        <span className={`text-[10px] font-medium transition-colors ${met ? 'text-green-700' : 'text-gray-400'
            }`}>{text}</span>
    </div>
);
